'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { cartState as recoilCartState } from '@/components/Context/CartProvider'
import { loginUserState } from '@/components/Context/JwtTokenProvider'
import { useSmoothScroll } from '@/components/hooks/useSmoothScroll'
import { getCart } from '@/serverActions/getCart'
import { getSampleProducts } from '@/serverActions/getSampleProducts'
import { getSimilarProducts } from '@/serverActions/getSimilarProducts'
import { CartType } from '@/types/cart'
import { ProductType } from '@/types/product'
import { UserType } from '@/types/user'

export default function CartPageClientLogic({
  initialCart,
  children,
}: {
  initialCart: CartType | null
  children: ({
    // eslint-disable-next-line no-unused-vars
    cart,
    // eslint-disable-next-line no-unused-vars
    loginUser,
    // eslint-disable-next-line no-unused-vars
    similarProducts,
    // eslint-disable-next-line no-unused-vars
    sampleProducts,
    // eslint-disable-next-line no-unused-vars
    reload,
  }: {
    cart: CartType | null
    loginUser: UserType | null
    similarProducts: ProductType[]
    sampleProducts: ProductType[]
    reload: (_scroll?: boolean) => void
  }) => ReactNode
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { scrollToTop } = useSmoothScroll()
  const loginUser = useRecoilValue(loginUserState)
  const [recoilCart, setRecoilCart] = useRecoilState(recoilCartState)
  const [similarProducts, setSimilarProducts] = useState<ProductType[]>([])
  const [sampleProducts, setSampleProducts] = useState<ProductType[]>([])
  const reload = useCallback(async () => {
    const latestCart = await getCart()
    setRecoilCart(latestCart)
  }, [])

  useEffect(() => {
    reload()
  }, [pathname, searchParams.toString()])

  useEffect(() => {
    if (initialCart) {
      setRecoilCart(initialCart)
    }
  }, [])

  useEffect(() => {
    getSimilarProducts({
      productIds: recoilCart?.invoice.invoiceItems.map((item) => item.cartProduct!.productId) || [],
    }).then((products) => {
      if (products != undefined && products != similarProducts) {
        setSimilarProducts(products)
      }
    })
    getSampleProducts({
      productIds: recoilCart?.invoice.invoiceItems.map((item) => item.cartProduct!.productId) || [],
    }).then((products) => {
      if (products != undefined && products != sampleProducts) {
        setSampleProducts(products)
      }
    })
  }, [recoilCart, pathname])

  return (
    <>
      {children({
        cart: recoilCart,
        loginUser,
        similarProducts,
        sampleProducts,
        reload: (scroll: boolean = true) => {
          reload()
          if (scroll) {
            scrollToTop()
          }
        },
      })}
    </>
  )
}
